import { isDefined } from '@sgme/fp';
import { createSafeContext } from '@sgme/react';
import classNames from 'classnames';

import 'components/discover/post-trade/life-cycle/index.css';
import { RenderPercent } from 'components/discover/post-trade/life-cycle/section/render-percent';
import { FormattedMessage } from 'components/shared/Intl';
import { IntlKey } from 'locales';
import { PropsWithChildren, RefObject, useRef, useState } from 'react';
import { BadgeProps, Popover, Badge as ReactBootStrapBadge, Overlay as ReactBootstrapOverlay } from 'react-bootstrap';

type ContextType = {
  isPopoverOpened: boolean;
  setIsPopoverOpened: (open: boolean) => void;
  infoIconRef: RefObject<HTMLElement | null>;
};
const [ContextProvider, useSafeContext] = createSafeContext('LifeCycleSectionContent', {} as ContextType);
function Provider({ children }: PropsWithChildren) {
  const [isPopoverOpened, setIsPopoverOpened] = useState(false);
  const infoIconRef = useRef<HTMLElement>(null);

  return <ContextProvider value={{ isPopoverOpened, setIsPopoverOpened, infoIconRef }}>{children}</ContextProvider>;
}

function Header() {
  const { setIsPopoverOpened, infoIconRef } = useSafeContext();
  return (
    <span ref={infoIconRef}>
      <em
        className="icon icon-sm clickable"
        onMouseEnter={() => setIsPopoverOpened(true)}
        onMouseLeave={() => setIsPopoverOpened(false)}
      >
        info_outline
      </em>
    </span>
  );
}
function Overlay({ children }: PropsWithChildren) {
  const { infoIconRef, isPopoverOpened } = useSafeContext();
  return (
    <ReactBootstrapOverlay target={infoIconRef as RefObject<HTMLElement>} show={isPopoverOpened} placement="bottom">
      <Popover>{children}</Popover>
    </ReactBootstrapOverlay>
  );
}
type TableHeaderProps = {
  translationKeys: IntlKey[];
};
function TableHeader({ translationKeys }: TableHeaderProps) {
  return (
    <thead>
      <tr className="border-bottom">
        {translationKeys.map(k => (
          <th key={k} className="border-top-0 text-primary">
            <span className="ms-1">
              <FormattedMessage id={k} />
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function TableCell({ children }: PropsWithChildren) {
  return <td className="float-start ms-1">{children}</td>;
}

function Badge({ children, ...props }: BadgeProps) {
  return (
    <ReactBootStrapBadge as="span" pill={true} bg="fake-color" className="border-0 badge-discreet-warning" {...props}>
      <span className="life-cycle-badge">{children}</span>
    </ReactBootStrapBadge>
  );
}
function Hit() {
  return (
    <Badge>
      <FormattedMessage id="discover.lifeCycle.hit" defaultMessage="hit" />
    </Badge>
  );
}

type DistanceToLimitProps = {
  value?: Nullable<number>;
};
function DistanceToLimit({ value }: DistanceToLimitProps) {
  return (
    <Badge className={classNames('border-0', toScoreLevel(value))}>
      <RenderPercent value={value} />
    </Badge>
  );
}

type Props = {
  distanceToLimit?: Nullable<number>;
  underlyings: readonly Underlying[];
};
type Underlying = {
  limitHit?: Nullable<boolean>;
  distanceToLimit?: Nullable<number>;
  bloombergCode: string;
};

function Content({ distanceToLimit: distance, underlyings }: Props) {
  const hasMultipleUnderlyings = underlyings.length > 1;
  const isMonoUnderlyingWithHit = underlyings.length === 1 && underlyings[0].limitHit;

  return hasMultipleUnderlyings ? (
    <Provider>
      <Header />
      <Overlay>
        <table className="table table-sm table-borderless mb-0 fs-6">
          <TableHeader translationKeys={['discover.lifeCycle.bloomberg', 'discover.lifeCycle.distance']} />
          <tbody className="text-secondary">
            {underlyings.map((underlying, underlyingIndex) => (
              <tr key={underlyingIndex} className="border-bottom">
                <TableCell>{underlying.bloombergCode}</TableCell>
                <td className="text-primary-alt float-end">
                  {underlying.limitHit ? <Hit /> : <DistanceToLimit value={underlying.distanceToLimit} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Overlay>
    </Provider>
  ) : (
    <>{isMonoUnderlyingWithHit ? <Hit /> : <DistanceToLimit value={distance} />}</>
  );
}

function toScoreLevel(score: number | null | undefined): string {
  if (isDefined(score) && score >= 0) {
    return 'badge-discreet-success';
  }
  if (isDefined(score) && score < 0) {
    return 'badge-discreet-warning';
  }

  return 'badge-discreet-info';
}

export const Section = {
  Content,
  Header,
  Overlay,
  Hit,
  DistanceToLimit,
  Badge,
  TableHeader,
  TableCell,
  Provider,
};
